import { useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';

export interface ISortState<T> {
  key: keyof T;
  dir: SortDir;
}

/**
 * Client-side sorting for the breakdown tables (rows are already fully loaded).
 * Sorting is stable per column: clicking the active column flips direction;
 * clicking a new column starts descending for numeric values (highest first) and
 * ascending for text — the intuitive default for each. `initial` is each panel's
 * default column: every panel opens on its own name column, ascending, so rows
 * land in a predictable order the reader can scan before they choose a ranking.
 */
export function useSortableRows<T>(rows: T[], initial: ISortState<T>) {
  const [sort, setSort] = useState<ISortState<T>>(initial);

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        // `null` means "not measured" — it is the ABSENCE of a value, not a low
        // one, so it never competes for either end. Unmeasured rows settle at
        // the bottom in both directions, instead of masquerading as the worst
        // performer descending and the best ascending.
        const aMissing = a[sort.key] === null;
        const bMissing = b[sort.key] === null;
        if (aMissing || bMissing) return aMissing === bMissing ? 0 : aMissing ? 1 : -1;
        const cmp = compareValues(a[sort.key], b[sort.key], sort.key);
        return sort.dir === 'asc' ? cmp : -cmp;
      }),
    [rows, sort],
  );

  const toggleSort = (key: keyof T) => {
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      const sample = rows.find((row) => row[key] != null)?.[key];
      return { key, dir: typeof sample === 'number' ? 'desc' : 'asc' };
    });
  };

  return {
    sorted,
    sortKey: sort.key,
    sortDir: sort.dir,
    toggleSort,
    /** Primitive fingerprint of the sort state — the panels feed it to
     *  usePagination's resetKey so a re-sort snaps back to the first page. */
    sortSignature: `${String(sort.key)}:${sort.dir}`,
  };
}

/** A single reused collator — built once, not per comparison (localeCompare would
 *  spin up locale machinery on every call). Cheap now, keeps sorting flat if a
 *  table ever grows large. */
const collator = new Intl.Collator();

/** Confidence is a severity enum, not free text — order it worst→best so the
 *  Confidence header surfaces problem rows first, instead of alphabetically
 *  (which would wedge 'high' between 'data-issue' and 'low'). */
const CONFIDENCE_RANK: Record<string, number> = {
  'data-issue': 0,
  low: 1,
  medium: 2,
  high: 3,
};

/** Numbers compare numerically; the confidence column by severity rank; everything
 *  else by localised string order. */
function compareValues(a: unknown, b: unknown, key: PropertyKey): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (key === 'confidence') {
    return (CONFIDENCE_RANK[String(a)] ?? -1) - (CONFIDENCE_RANK[String(b)] ?? -1);
  }
  return collator.compare(String(a ?? ''), String(b ?? ''));
}
